import {
  errorLogger,
  getErrorMessages,
  LoadingTypes,
  RequestFailureMessage,
  startLoading,
  stopLoading,
  updateAppAlert,
} from '@labrav/common'
import { AxiosError, AxiosResponse } from 'axios'
import { camelize } from 'casing'
import { AnyAction } from '@reduxjs/toolkit'

import type { AppDispatch, RootState } from '../../store/types'
import { CloudMarketplace } from '../../modules/productsListing/types'
import { getSubscriptionsAPIBasedOnCloud } from '../subscription/action'

export enum ModalSubscriptionsActionTypes {
  SET_MODAL_SUBSCRIPTIONS_DATA = 'SET_MODAL_SUBSCRIPTIONS_DATA',
  CLEAR_MODAL_SUBSCRIPTIONS_DATA = 'CLEAR_MODAL_SUBSCRIPTIONS_DATA',
}

export const getModalSubscriptions =
  ({
    partnerId,
    productId,
    cloudMarketplace,
    pageSize,
    pageNumber,
    crmOpportunityId,
    input,
    selectedInput,
    sectionName,
    status,
    expiringDays,
    privateOfferId,
    sortBy,
    sortOrder,
    contractStatusUpdate = false,
  }: {
    partnerId: string
    productId?: string
    cloudMarketplace: CloudMarketplace
    pageSize?: number
    pageNumber?: number
    crmOpportunityId?: string
    input?: string
    selectedInput?: string
    sectionName?: string
    status?: string
    expiringDays?: string
    privateOfferId?: string
    sortBy?: string
    sortOrder?: 'asc' | 'desc'
    contractStatusUpdate?: boolean
  }) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    await dispatch(startLoading(LoadingTypes.GENERAL))
    try {
      const isGcp = cloudMarketplace === 'GCP'
      const response = await getSubscriptionsAPIBasedOnCloud(
        partnerId,
        cloudMarketplace,
        productId,
        pageSize,
        pageNumber,
        crmOpportunityId,
        input,
        selectedInput,
        sectionName,
        status,
        expiringDays,
        privateOfferId,
        isGcp ? sortBy : undefined,
        isGcp ? sortOrder : undefined,
        contractStatusUpdate
      )
      dispatch(
        setModalSubscriptionsData(
          camelize({
            ...(response?.data as Record<string, unknown>),
            pageNumber,
          })
        )
      )
    } catch (error: unknown) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<
        ErrorResponse & { message?: string }
      >

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState: globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      await dispatch(stopLoading(LoadingTypes.GENERAL))
    }
  }

export const clearModalSubscriptions = () =>
  ({
    type: ModalSubscriptionsActionTypes.CLEAR_MODAL_SUBSCRIPTIONS_DATA,
  }) as unknown as AnyAction

export const setModalSubscriptionsData = (data: Record<string, unknown>) =>
  ({
    type: ModalSubscriptionsActionTypes.SET_MODAL_SUBSCRIPTIONS_DATA,
    payload: {
      data: data.subscriptions || [],
      pageNumber: data.pageNumber,
      count: data.count ?? 0,
      sortBy: data.sortBy ?? '',
      sortOrder: data.sortOrder ?? 'desc',
    },
  }) as unknown as AnyAction

/** Flyout call-site alias; prefer setModalSubscriptionsData */
export const setSubscriptionsData = setModalSubscriptionsData
